// src/admin/components/HomePageConfig/InstantQuoteForm.jsx
import React from "react";
import ColorSection from "./ColorSection";
import ColorPicker from "./ColorPicker";
import FontSizeSection from "./FontSizeSection";

export default function InstantQuoteForm({
  instantQuoteForm,
  setInstantQuoteForm,
  availableForms = [],
  uploadImageFile,
  deleteImageFile,
  IMAGES_BASE
}) {
  
  // Provide safe defaults
  const safeInstantQuoteForm = {
    display: false,
    formId: "",
    position: "afterAboutUs",
    sectionTitle: "Get Instant Quotes",
    sectionDescription: "Fill out the form below to get a free quote",
    backgroundImage: "",
    backgroundColor: "",
    overlayOpacity: 0.5,
    titleColor: "#000000",
    descriptionColor: "#333333",
    formBgColor: "#ffffff",
    formTextColor: "#333333",
    buttonColor: "#ffffff",
    buttonBgColor: "#000000",
    buttonHoverColor: "#ffffff",
    buttonHoverBgColor: "#333333",
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
    sideContent: {
      enabled: true,
      headingTop: "WHY CHOOSE",
      headingHighlight: "REGENCY INTERIOR",
      bullets: [
        "Custom furniture and interiors tailored to your needs.",
        "Durable woodwork with marine ply and natural wood.",
        "Premium hardware from Hettich, Blum, and Godrej.",
        "Smart storage with stainless steel and tandem baskets.",
        "Expert renovation services for modern spaces.",
      ],
      footerLine: "REACH OUT TODAY TO DISCUSS YOUR VISION!",
      headingColor: "#000000",
      highlightColor: "#d4af37",
      bulletColor: "#333333",
      bulletIconColor: "#d4af37",
      footerColor: "#000000",
      useSectionColors: true,
    },
    ...instantQuoteForm
  };

  const safeAvailableForms = availableForms || [];
  
  const handleInstantQuoteBackgroundUpload = async (file) => {
    if (!file) return;
    const filename = await uploadImageFile(file);
    if (filename) {
      setInstantQuoteForm((prev) => ({ ...prev, backgroundImage: filename }));
    }
  };

  const handleInstantQuoteBackgroundDelete = async () => {
    if (!safeInstantQuoteForm.backgroundImage) return;
    const ok = await deleteImageFile(safeInstantQuoteForm.backgroundImage);
    if (ok) {
      setInstantQuoteForm((prev) => ({ ...prev, backgroundImage: "" }));
    }
  };

  // Color handlers for main form
  const updateFormColor = (property, value) => {
    setInstantQuoteForm((prev) => ({
      ...prev,
      [property]: value,
    }));
  };

  // Color handlers for side content
  const updateSideContentColor = (property, value) => {
    setInstantQuoteForm((prev) => ({
      ...prev,
      sideContent: {
        ...prev.sideContent,
        [property]: value,
      },
    }));
  };

  // Process color value for saving
  const processColorValue = (value) => {
    if (value === "transparent" || value === "" || !value) {
      return "transparent";
    }
    return value;
  };

  const toggleMainSectionColors = (useSectionColors) => {
    setInstantQuoteForm(prev => ({
      ...prev,
      useSectionColors
    }));
  };

  const toggleSideContentColors = (useSectionColors) => {
    setInstantQuoteForm(prev => ({
      ...prev,
      sideContent: {
        ...prev.sideContent,
        useSectionColors
      }
    }));
  };

  const toggleSectionFontSizes = (useSectionFontSizes) => {
    setInstantQuoteForm((prev) => ({
      ...prev,
      useSectionFontSizes,
    }));
  };

  const handleFontSizeChange = (key, value) => {
    setInstantQuoteForm((prev) => ({
      ...prev,
      fontSizes: {
        ...(prev?.fontSizes || {}),
        [key]: value,
      },
    }));
  };

  return (
    <div className="hcfg-card">
      <div className="hcfg-headrow">
        <h3>Instant Quote Form</h3>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <input
            type="checkbox"
            checked={safeInstantQuoteForm.display}
            onChange={(e) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                display: e.target.checked,
              }))
            }
            style={{ width: "18px", height: "18px", cursor: "pointer" }}
          />
          Show Quote Form on Home Page
        </label>
      </div>

      {/* Form Selection and Position */}
      <div
        className="hcfg-row"
        style={{
          opacity: safeInstantQuoteForm.display ? 1 : 0.6,
          pointerEvents: safeInstantQuoteForm.display ? "auto" : "none",
        }}
      >
        <div className="hcfg-col">
          <label>Select Form</label>
          <select
            value={safeInstantQuoteForm.formId}
            onChange={(e) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                formId: e.target.value,
              }))
            }
            style={{ width: "100%", padding: "8px" }}
          >
            <option value="">-- Select a form --</option>
            {safeAvailableForms.map((form) => (
              <option key={form.id} value={form.id}>
                {form.name} - {form.title}
              </option>
            ))}
          </select>
          {safeAvailableForms.length === 0 && (
            <p style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
              No forms found. Create forms in the Form Config section first.
            </p>
          )}
        </div>

        <div className="hcfg-col">
          <label>Form Position</label>
          <select
            value={safeInstantQuoteForm.position}
            onChange={(e) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                position: e.target.value,
              }))
            }
            style={{ width: "100%", padding: "8px" }}
          >
            <option value="beforeHero">Before Hero Section</option>
            <option value="afterHero">After Hero Section</option>
            <option value="afterAboutUs">After About Us</option>
            <option value="afterServices">After Services</option>
            <option value="afterReviews">After Customer Reviews</option>
            <option value="sidebarRight">Right Sidebar</option>
            <option value="sidebarLeft">Left Sidebar</option>
          </select>
        </div>
      </div>

      {/* Section Title & Description */}
      <div
        className="hcfg-row"
        style={{
          opacity: safeInstantQuoteForm.display ? 1 : 0.6,
          pointerEvents: safeInstantQuoteForm.display ? "auto" : "none",
        }}
      >
        <div className="hcfg-col">
          <label>Section Title</label>
          <input
            value={safeInstantQuoteForm.sectionTitle || ""}
            onChange={(e) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                sectionTitle: e.target.value,
              }))
            }
            placeholder="Get Instant Quotes"
            style={{ width: "100%", padding: "8px" }}
          />
        </div>
        <div className="hcfg-col">
          <label>Section Description</label>
          <textarea
            rows={2}
            value={safeInstantQuoteForm.sectionDescription || ""}
            onChange={(e) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                sectionDescription: e.target.value,
              }))
            }
            placeholder="Fill out the form below to get a free quote"
            style={{ width: "100%", padding: "8px", resize: "vertical" }}
          />
        </div>
      </div>

      {/* Form Color Customization */}
      <ColorSection
        title="Form Section"
        colors={{
          headingColor: safeInstantQuoteForm.titleColor || "#000000",
          subHeadingColor: safeInstantQuoteForm.descriptionColor || "#333333",
          contentColor: safeInstantQuoteForm.formTextColor || "#333333",
          buttonTextColor: safeInstantQuoteForm.buttonColor || "#ffffff",
          buttonBgColor: safeInstantQuoteForm.buttonBgColor || "#000000",
        }}
        onColorChange={(property, value) => {
          const propertyMap = {
            headingColor: "titleColor",
            subHeadingColor: "descriptionColor",
            contentColor: "formTextColor",
            buttonTextColor: "buttonColor",
            buttonBgColor: "buttonBgColor",
          };
          updateFormColor(propertyMap[property], processColorValue(value));
        }}
        useSectionColors={safeInstantQuoteForm.useSectionColors !== false}
        onToggleSectionColors={toggleMainSectionColors}
      />

      <FontSizeSection
        title="Form Section"
        sizes={safeInstantQuoteForm.fontSizes || {}}
        onChange={handleFontSizeChange}
        useSectionFontSizes={safeInstantQuoteForm.useSectionFontSizes === true}
        onToggleSectionFontSizes={toggleSectionFontSizes}
        fields={[
          { key: "heading", label: "Form Title Size", placeholder: "e.g., 24px" },
          { key: "content", label: "Form Description Size", placeholder: "e.g., 14px" },
          { key: "button", label: "Button Text Size", placeholder: "e.g., 14px" },
          { key: "sideHeading", label: "Side Heading Size", placeholder: "e.g., 14px" },
          { key: "sideHighlight", label: "Side Highlight Size", placeholder: "e.g., 32px" },
          { key: "sideBullet", label: "Side Bullet Size", placeholder: "e.g., 16px" },
          { key: "sideFooter", label: "Side Footer Size", placeholder: "e.g., 14px" },
        ]}
      />

      {/* Background Image for Form Section */}
      <div
        className="hcfg-row"
        style={{
          opacity: safeInstantQuoteForm.display ? 1 : 0.6,
          pointerEvents: safeInstantQuoteForm.display ? "auto" : "none",
          marginTop: "20px",
          paddingTop: "20px",
          borderTop: "1px solid #eee",
        }}
      >
        <div className="hcfg-col img">
          <h4 style={{ marginBottom: "10px" }}>Form Section Background</h4>
          {safeInstantQuoteForm.backgroundImage ? (
            <img
              src={`${IMAGES_BASE}/${encodeURIComponent(
                safeInstantQuoteForm.backgroundImage
              )}`}
              alt="Background preview"
              className="hcfg-preview"
              style={{ 
                maxHeight: "200px", 
                width: "100%", 
                objectFit: "cover",
                borderRadius: "4px"
              }}
            />
          ) : (
            <div className="hcfg-placeholder" style={{
              height: "150px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f5f5f5",
              borderRadius: "4px",
              color: "#666"
            }}>
              No background image
            </div>
          )}
          <div className="hcfg-actions-inline" style={{ marginTop: "10px" }}>
            <label className="hcfg-btn" style={{
              padding: "8px 16px",
              background: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              display: "inline-block"
            }}>
              Upload Background
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleInstantQuoteBackgroundUpload(e.target.files?.[0])
                }
                style={{ display: "none" }}
              />
            </label>
            <button
              className="hcfg-btn danger"
              onClick={handleInstantQuoteBackgroundDelete}
              style={{
                padding: "8px 16px",
                background: "#f44336",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                marginLeft: "10px"
              }}
            >
              Delete Background
            </button>
          </div>
        </div>

        <div className="hcfg-col">
          <ColorPicker
            label="Background Color (optional)"
            value={safeInstantQuoteForm.backgroundColor || "#ffffff"}
            onChange={(value) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                backgroundColor: processColorValue(value),
              }))
            }
            description="Set to 'transparent' for no background color"
            allowTransparent={true}
          />
          
          <div style={{ marginTop: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Background Overlay Opacity</label>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={safeInstantQuoteForm.overlayOpacity}
                onChange={(e) =>
                  setInstantQuoteForm((prev) => ({
                    ...prev,
                    overlayOpacity: parseFloat(e.target.value),
                  }))
                }
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: "30px", textAlign: "center" }}>
                {Number(safeInstantQuoteForm.overlayOpacity).toFixed(1)}
              </span>
            </div>
            <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
              Controls the darkness of overlay over background image (0 = no overlay, 1 = fully dark)
            </small>
          </div>
        </div>
      </div>

      {/* Right Side Content editor */}
      <div
        className="hcfg-row"
        style={{
          opacity: safeInstantQuoteForm.display ? 1 : 0.6,
          pointerEvents: safeInstantQuoteForm.display ? "auto" : "none",
          marginTop: "20px",
          paddingTop: "20px",
          borderTop: "1px solid #eee",
        }}
      >
        <div className="hcfg-col">
          <h4 style={{ marginBottom: 10 }}>Right Side Content (Beside Form)</h4>

          <label style={{ display: "block", marginBottom: "5px" }}>Show Right Content</label>
          <select
            value={safeInstantQuoteForm.sideContent?.enabled ? "yes" : "no"}
            onChange={(e) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                sideContent: {
                  ...(prev.sideContent || {}),
                  enabled: e.target.value === "yes",
                },
              }))
            }
            style={{ width: "100%", padding: "8px", marginBottom: "15px" }}
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>

          <label style={{ display: "block", marginBottom: "5px" }}>Top Heading</label>
          <input
            value={safeInstantQuoteForm.sideContent?.headingTop || ""}
            onChange={(e) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                sideContent: {
                  ...(prev.sideContent || {}),
                  headingTop: e.target.value,
                },
              }))
            }
            placeholder="WHY CHOOSE"
            style={{ width: "100%", padding: "8px", marginBottom: "15px" }}
          />

          <label style={{ display: "block", marginBottom: "5px" }}>Highlight Heading</label>
          <input
            value={safeInstantQuoteForm.sideContent?.headingHighlight || ""}
            onChange={(e) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                sideContent: {
                  ...(prev.sideContent || {}),
                  headingHighlight: e.target.value,
                },
              }))
            }
            placeholder="REGENCY INTERIOR"
            style={{ width: "100%", padding: "8px", marginBottom: "15px" }}
          />

          <label style={{ display: "block", marginBottom: "5px" }}>Bottom Line</label>
          <textarea
            rows={2}
            value={safeInstantQuoteForm.sideContent?.footerLine || ""}
            onChange={(e) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                sideContent: {
                  ...(prev.sideContent || {}),
                  footerLine: e.target.value,
                },
              }))
            }
            placeholder="REACH OUT TODAY TO DISCUSS YOUR VISION!"
            style={{ width: "100%", padding: "8px", resize: "vertical" }}
          />
        </div>

        <div className="hcfg-col">
          <label style={{ display: "block", marginBottom: "5px" }}>Bullet Points (one per line)</label>
          <textarea
            rows={10}
            value={(safeInstantQuoteForm.sideContent?.bullets || []).join("\n")}
            onChange={(e) =>
              setInstantQuoteForm((prev) => ({
                ...prev,
                sideContent: {
                  ...(prev.sideContent || {}),
                  bullets: e.target.value
                    .split("\n")
                    .map((x) => x.trim())
                    .filter(Boolean),
                },
              }))
            }
            placeholder={`Custom furniture...\nDurable woodwork...\nPremium hardware...`}
            style={{ width: "100%", padding: "8px", resize: "vertical" }}
          />
          <small style={{ color: "#666", display: "block", marginTop: "5px" }}>
            This content will appear beside the form like your screenshot.
          </small>
        </div>
      </div>

      {/* Right Side Content Colors */}
      <ColorSection
        title="Side Content"
        colors={{
          headingColor: safeInstantQuoteForm.sideContent?.headingColor || "#000000",
          subHeadingColor: safeInstantQuoteForm.sideContent?.highlightColor || "#d4af37",
          contentColor: safeInstantQuoteForm.sideContent?.bulletColor || "#333333",
          buttonTextColor: safeInstantQuoteForm.sideContent?.bulletIconColor || "#d4af37",
          buttonBgColor: safeInstantQuoteForm.sideContent?.footerColor || "#000000",
        }}
        onColorChange={(property, value) => {
          const sideColorMap = {
            headingColor: "headingColor",
            subHeadingColor: "highlightColor",
            contentColor: "bulletColor",
            buttonTextColor: "bulletIconColor",
            buttonBgColor: "footerColor",
          };
          updateSideContentColor(sideColorMap[property], processColorValue(value));
        }}
        useSectionColors={safeInstantQuoteForm.sideContent?.useSectionColors !== false}
        onToggleSectionColors={toggleSideContentColors}
      />
    </div>
  );
}
